<!-- Patient_CareGraph.vue -->
<!--
  Care Graph tab — EPISODE-CENTRIC layout (redesigned 2026-07-26).

  WHY THIS SHAPE. The graph is organised under EPISODE nodes (one per discharge):
  a signal `surfaced_in` exactly one episode, which is what gives the readmission
  analysis its per-discharge attribution. A flat patient-level list therefore
  misrepresents the data — one real IHCI patient carries 22 episodes and 15
  signals, and shown flat there is no way to tell which discharge surfaced which
  barrier. So episodes are the outer grouping, newest discharge first.

  WHAT THE PREVIOUS VERSION GOT WRONG (all three failed SILENTLY — no error, just
  missing content — which is why they survived so long):
    1. RESOLUTION HANGS OFF THE TO-DO, not the signal. It read `resolves` edges
       targeting the SIGNAL; 12,147 of 12,264 target a TODO, so 99% of
       resolutions rendered nowhere.
    2. OUTCOME VOCABULARY is done | unable_to_complete. It tested
       `outcome === 'resolved'` (retired), so 12,244 of 12,361 resolutions fell
       to the red/failure branch.
    3. EVIDENCE IS INLINE on each node as data.evidence[]. It read evidence NODES
       via `derived_from` edges — a legacy path covering a minority of nodes.

  DERIVED STATUS. signal.data.status is NULL on 87% of signals (the one-process
  rewrite dropped _recompute_signal_statuses), so status is DERIVED HERE from
  to-do completion, per the documented rule: resolved = every to-do has a `done`
  resolution; unable_to_resolve = all closed to-dos closed unable; open = a to-do
  still outstanding; not_addressed = no to-dos. A stored status is only used as a
  fallback when there are no to-dos to derive from.

  CLOSURE GRADE IS SURFACED DELIBERATELY. verified_terminal ("the prescription was
  filled") vs in_progress_step / soft_action ("they were referred") is the
  distinction the readmission study found decisive — only the terminal act
  carries benefit. It was invisible before.
-->
<template>
  <div class="h-full flex flex-col bg-gray-50">
    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex flex-col min-h-0">
      <div class="px-4 py-3 border-b border-gray-200 bg-white shrink-0">
        <div class="flex items-center gap-2">
          <div class="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-3 w-36 bg-gray-100 rounded animate-pulse"></div>
        </div>
        <div class="flex items-center gap-1.5 mt-2">
          <div v-for="n in 3" :key="'sk-filter-' + n" class="h-5 rounded-full bg-gray-200 animate-pulse" :style="{ width: [48, 56, 44][n - 1] + 'px' }"></div>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto p-4 space-y-4">
        <div v-for="ep in 2" :key="'sk-ep-' + ep" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
            <div class="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
            <div class="h-3.5 w-32 bg-gray-200 rounded animate-pulse"></div>
            <div class="h-3 w-20 bg-gray-100 rounded animate-pulse ml-auto"></div>
          </div>
          <div class="p-4 space-y-3">
            <div v-for="s in (ep === 1 ? 3 : 2)" :key="'sk-sig-' + s" class="border border-gray-100 rounded-lg p-3">
              <div class="flex items-center justify-between mb-2">
                <div class="h-3.5 bg-gray-200 rounded animate-pulse" :style="{ width: [160, 120, 140][s - 1] + 'px' }"></div>
                <div class="h-4 w-14 bg-gray-100 rounded-full animate-pulse"></div>
              </div>
              <div class="space-y-1.5 ml-4">
                <div v-for="t in 2" :key="'sk-todo-' + t" class="flex items-center gap-2">
                  <div class="w-3.5 h-3.5 rounded border border-gray-200 bg-gray-100 animate-pulse shrink-0"></div>
                  <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: t === 1 ? '65%' : '50%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex-1 flex flex-col min-h-0">
      <!-- HEADER -->
      <div class="px-4 py-3 border-b border-gray-200 bg-white shrink-0">
        <div class="flex items-center gap-2">
          <FontAwesomeIcon :icon="['fas', 'diagram-project']" class="text-gray-500 text-sm" />
          <span class="text-sm font-semibold text-gray-900">Care Graph</span>
          <span v-if="signals.length" class="text-xs text-gray-400">
            {{ openCount }} open · {{ signals.length }} {{ signals.length === 1 ? 'problem' : 'problems' }}
            <template v-if="episodeGroups.length"> · {{ episodeGroups.length }} {{ episodeGroups.length === 1 ? 'episode' : 'episodes' }}</template>
          </span>
          <button
            class="ml-auto px-2.5 py-1 border border-gray-300 text-gray-600 text-xs font-medium rounded hover:bg-gray-50 flex items-center gap-1.5"
            @click="openNewTodo"
          >
            <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[10px]" />
            New to-do
          </button>
          <button
            :disabled="rebuilding"
            class="px-2.5 py-1 bg-primary-600 text-white text-xs font-medium rounded hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5"
            @click="rebuildCarePlan"
          >
            <FontAwesomeIcon :icon="['fas', rebuilding ? 'spinner' : 'wand-magic-sparkles']" :class="{ 'animate-spin': rebuilding }" />
            {{ rebuilding ? 'Rebuilding…' : (hasGraph ? 'Rebuild' : 'Generate') }}
          </button>
        </div>

        <!-- Filters. High-leverage + open are the two views a care manager
             actually works from, so they are one click rather than a menu. -->
        <div v-if="hasGraph" class="flex items-center gap-1.5 mt-2">
          <button
            v-for="f in filterOptions"
            :key="f.key"
            class="px-2 py-0.5 rounded-full text-[10.5px] font-medium border transition-colors"
            :class="filter === f.key
              ? 'bg-gray-800 border-gray-800 text-white'
              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'"
            @click="filter = f.key"
          >{{ f.label }}<span v-if="f.count !== null" class="opacity-60"> {{ f.count }}</span></button>
          <button
            class="ml-auto text-[10.5px] text-gray-400 hover:text-gray-600"
            @click="toggleAllEpisodes"
          >{{ allExpanded ? 'Collapse all' : 'Expand all' }}</button>
        </div>
      </div>

      <!-- New to-do form: choose an existing signal OR create a new one -->
      <div v-if="newTodo.open" class="px-4 py-3 border-b border-gray-200 bg-primary-50/40 shrink-0 space-y-2">
        <div class="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">New to-do</div>
        <div class="flex items-center gap-2">
          <label class="text-xs text-gray-500 w-16 shrink-0">Signal</label>
          <select
            v-if="newTodo.mode === 'existing'"
            v-model="newTodo.signalId"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Select a signal…</option>
            <option v-for="s in signals" :key="s.id" :value="s.id">{{ s.data.title }}</option>
          </select>
          <input
            v-else
            v-model="newTodo.newSignalTitle"
            type="text"
            placeholder="New signal (problem) name…"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-1 focus:ring-primary-500"
          />
          <button class="text-[11px] text-primary-600 hover:underline shrink-0" @click="toggleSignalMode">
            {{ newTodo.mode === 'existing' ? '+ New signal' : 'Pick existing' }}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-xs text-gray-500 w-16 shrink-0">To-do</label>
          <input
            v-model="newTodo.title"
            type="text"
            placeholder="What needs doing…"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-1 focus:ring-primary-500"
            @keyup.enter="submitNewTodo"
          />
        </div>
        <div class="flex items-start gap-2">
          <label class="text-xs text-gray-500 w-16 shrink-0 mt-1.5">Reason</label>
          <textarea
            v-model="newTodo.summary"
            rows="2"
            placeholder="Why, and what the other person needs to know…"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-1 focus:ring-primary-500"
            data-demo-blur
          ></textarea>
        </div>
        <div class="flex items-center gap-2">
          <label class="text-xs text-gray-500 w-16 shrink-0">Assign to</label>
          <select
            v-model="newTodo.assignee"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Nobody yet</option>
            <option v-for="m in members" :key="m.id" :value="m.id">{{ m.name }}{{ m.kind === 'team' ? ' (team)' : '' }}</option>
          </select>
        </div>
        <div class="flex items-center justify-end gap-2">
          <button class="text-xs px-2 py-1 text-gray-500 hover:text-gray-700" @click="newTodo.open = false">Cancel</button>
          <button
            class="text-xs px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            :disabled="!canSubmitNewTodo"
            @click="submitNewTodo"
          >Add</button>
        </div>
      </div>

      <!-- Empty -->
      <div v-if="!hasGraph" class="flex-1 flex items-center justify-center text-xs text-gray-400 px-4 text-center">
        No care graph yet — click Generate to build it from this patient's history.
      </div>

      <!-- EPISODE-GROUPED BODY -->
      <div v-else class="flex-1 overflow-y-auto p-3 space-y-2">
        <div v-if="!visibleGroups.length" class="text-center text-xs text-gray-400 py-8">
          Nothing matches this filter.
        </div>

        <div
          v-for="grp in visibleGroups"
          :key="grp.key"
          class="bg-white rounded-lg border border-gray-200 overflow-hidden"
        >
          <!-- EPISODE HEADER -->
          <button
            class="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
            @click="toggleEpisode(grp.key)"
          >
            <FontAwesomeIcon
              :icon="['fas', expandedEpisodes[grp.key] ? 'chevron-down' : 'chevron-right']"
              class="text-[10px] text-gray-400 mt-1 shrink-0"
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5 flex-wrap">
                <span class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide"
                      :class="grp.episode ? 'bg-slate-200 text-slate-700' : 'bg-gray-100 text-gray-500'">
                  {{ grp.episode ? (grp.episodeType || 'Episode') : 'No discharge' }}
                </span>
                <span class="text-xs font-semibold text-gray-900">{{ grp.dateLabel }}</span>
                <span v-if="grp.facility" class="text-[11px] text-gray-400 truncate max-w-[180px]" :title="grp.facility">
                  {{ grp.facility }}
                </span>
              </div>
              <!-- Counts, and the high-leverage marker that makes an episode worth opening. -->
              <div class="flex items-center gap-2 mt-0.5 text-[10.5px] text-gray-500">
                <span>{{ grp.signals.length }} {{ grp.signals.length === 1 ? 'problem' : 'problems' }}</span>
                <span v-if="grp.openCount" class="text-amber-600 font-medium">{{ grp.openCount }} open</span>
                <span v-if="grp.resolvedCount" class="text-green-700">{{ grp.resolvedCount }} resolved</span>
                <span v-if="grp.leverageCount" class="text-violet-700 font-medium">
                  <FontAwesomeIcon :icon="['fas', 'bolt']" class="text-[9px]" />
                  {{ grp.leverageCount }} high-leverage
                </span>
                <span v-if="grp.terminalCount" class="text-green-700 font-medium">
                  <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-[9px]" />
                  {{ grp.terminalCount }} verified
                </span>
              </div>
            </div>
          </button>

          <!-- Episode clinical summary (LLM-authored, only worth showing when open) -->
          <div v-if="expandedEpisodes[grp.key] && grp.summary" class="px-3 pb-2 pl-8 -mt-1">
            <p class="text-[11px] text-gray-500 leading-relaxed">{{ grp.summary }}</p>
          </div>

          <!-- SIGNALS WITHIN THE EPISODE -->
          <div v-if="expandedEpisodes[grp.key]" class="border-t border-gray-100 p-3 space-y-3 bg-gray-50/50">
            <div v-for="sig in grp.signals" :key="sig.id" :data-signal="sig.id" class="scroll-mt-2">
              <!-- SIGNAL CARD -->
              <div
                class="bg-white rounded-lg border shadow-sm"
                :class="sig._status === 'resolved' ? 'border-gray-200 opacity-75' : (sig._leverage ? 'border-violet-200' : 'border-gray-200')"
              >
                <div class="flex items-start gap-2.5 p-3">
                  <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" :class="statusDot(sig._status)"></span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-700">Signal</span>
                      <span class="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide" :class="statusPillClass(sig._status)">
                        {{ statusLabel(sig._status) }}
                      </span>
                      <!-- HIGH-LEVERAGE. A barrier whose closure has a short, direct,
                           evidence-based path to a 30-day readmission. -->
                      <span
                        v-if="sig._leverage"
                        class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 inline-flex items-center gap-1"
                        :title="'High-leverage loop: ' + labelize(sig.data.slam_dunk_type)"
                      >
                        <FontAwesomeIcon :icon="['fas', 'bolt']" class="text-[8px]" />
                        {{ labelize(sig.data.slam_dunk_type) }}
                      </span>
                      <span
                        v-if="sig.data.readmission_relevant"
                        class="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-rose-50 text-rose-600 border border-rose-100"
                        :title="sig.data.readmission_pathway ? 'Readmission pathway: ' + labelize(sig.data.readmission_pathway) : 'Readmission-relevant'"
                      >readmit risk</span>
                    </div>
                    <div class="text-sm font-semibold text-gray-900 leading-snug" :class="{ 'line-through': sig._status === 'resolved' }">
                      {{ sig.data.title }}
                    </div>
                    <p v-if="sig.data.summary" class="text-xs text-gray-500 mt-1 leading-relaxed">{{ sig.data.summary }}</p>

                    <!-- Clinical context the model carries and nothing rendered before. -->
                    <div v-if="signalMeta(sig).length" class="flex items-center gap-x-3 gap-y-1 flex-wrap mt-1.5">
                      <span v-for="m in signalMeta(sig)" :key="m.label" class="text-[10.5px] text-gray-400">
                        {{ m.label }} <span class="text-gray-600 font-medium">{{ m.value }}</span>
                      </span>
                    </div>

                    <!-- Underlying facts (`due_to` edges) — 3,623 of these existed
                         and were never rendered. -->
                    <div v-if="dueToFor(sig.id).length" class="mt-1.5 text-[11px]">
                      <span class="text-gray-400">Because: </span>
                      <span v-for="(f, i) in dueToFor(sig.id)" :key="f.id" class="text-gray-500">
                        {{ f.data.title }}<span v-if="i < dueToFor(sig.id).length - 1">; </span>
                      </span>
                    </div>

                    <!-- Related signals -->
                    <div v-if="relatedFor(sig.id).length" class="mt-1.5">
                      <span class="text-[11px] text-gray-400">Related: </span>
                      <span v-for="(rel, i) in relatedFor(sig.id)" :key="rel.id" class="text-[11px] text-gray-500">
                        <button class="hover:text-primary-600 hover:underline" @click="scrollToSignal(rel.id)">{{ rel.data.title }}</button><span v-if="i < relatedFor(sig.id).length - 1">, </span>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Evidence disclosure (INLINE evidence, legacy nodes as fallback) -->
                <div v-if="evidenceFor(sig).length" class="border-t border-gray-100 px-3 py-1.5">
                  <button class="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600" @click="toggleEvidence(sig.id)">
                    <FontAwesomeIcon :icon="['fas', expandedEvidence[sig.id] ? 'chevron-down' : 'chevron-right']" class="text-[9px]" />
                    {{ evidenceFor(sig).length }} {{ evidenceFor(sig).length === 1 ? 'source' : 'sources' }}
                  </button>
                  <div v-if="expandedEvidence[sig.id]" class="mt-1.5 space-y-1.5 pl-3">
                    <div v-for="(ev, i) in evidenceFor(sig)" :key="i" class="flex gap-1.5 text-[11px]">
                      <FontAwesomeIcon :icon="evidenceIcon(ev.evidence_type)" class="text-gray-300 shrink-0 mt-0.5" />
                      <div class="min-w-0">
                        <span class="font-medium text-gray-500">{{ ev.source }}</span>
                        <span v-if="ev.text" class="text-gray-400"> — “{{ ev.text }}”</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- TO-DOS, each with ITS OWN resolution nested beneath it.
                   This is the structural fix: a resolution is the COMPLETION OF A
                   TO-DO (`resolves` -> todo), not a judgement about the signal. -->
              <div v-if="todosFor(sig.id).length" class="mt-1.5 ml-4 space-y-1.5">
                <div v-for="td in todosFor(sig.id)" :key="td.id" class="space-y-1.5">
                  <div class="flex items-start gap-1.5 group">
                    <FontAwesomeIcon :icon="['fas', 'arrow-turn-down-right']" class="text-gray-300 text-[11px] mt-2.5 shrink-0" />
                    <div class="flex-1 min-w-0 bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-2">
                      <div class="flex items-start gap-2">
                        <span class="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" :class="todoDot(td)"></span>
                        <span class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 shrink-0">To-Do</span>
                        <span class="text-xs flex-1" :class="todoClosed(td) ? 'text-gray-400 line-through' : 'text-gray-700'">
                          {{ td.data.title }}
                        </span>
                        <span class="text-[10px] shrink-0 mt-0.5 uppercase tracking-wide" :class="todoStateClass(td)">
                          {{ todoStateLabel(td) }}
                        </span>
                        <button
                          class="shrink-0 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                          title="Delete to-do"
                          @click="deleteTodo(td.id)"
                        >
                          <FontAwesomeIcon :icon="['fas', 'trash-can']" class="text-[11px]" />
                        </button>
                      </div>
                      <p v-if="td.data.summary" class="text-[11px] text-gray-500 mt-1 pl-3 leading-relaxed">{{ td.data.summary }}</p>
                      <div class="mt-1.5 pl-3">
                        <CareGraphAssignee
                          :owner-entity-id="ownerFor(td.id)"
                          :owner-name="ownerNameFor(td.id)"
                          :members="members"
                          @assign="mid => onReassign(td.id, mid)"
                          @unassign="onReassign(td.id, null)"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- RESOLUTION(S) for this to-do, indented one level further.
                       CLOSURE GRADE drives the colour: only verified_terminal is
                       green, because only the terminal act carries the readmission
                       benefit — a referral or a booking is a near-miss. -->
                  <div
                    v-for="res in resolutionsForTodo(td.id)"
                    :key="res.id"
                    class="flex items-start gap-1.5 ml-5"
                  >
                    <FontAwesomeIcon :icon="['fas', 'arrow-turn-down-right']" class="text-gray-300 text-[11px] mt-2.5 shrink-0" />
                    <div class="flex-1 min-w-0 rounded-lg border shadow-sm px-3 py-2 flex items-start gap-2" :class="resolutionBox(res)">
                      <FontAwesomeIcon :icon="['fas', resolutionIcon(res)]" class="text-xs shrink-0 mt-0.5" :class="resolutionIconClass(res)" />
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide" :class="resolutionBadge(res)">
                            {{ resolutionDone(res) ? 'Resolution' : 'Could not complete' }}
                          </span>
                          <span
                            v-if="res.data.closure_grade"
                            class="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide"
                            :class="gradeClass(res.data.closure_grade)"
                            :title="gradeHint(res.data.closure_grade)"
                          >{{ gradeLabel(res.data.closure_grade) }}</span>
                        </div>
                        <div class="text-xs font-medium" :class="resolutionTextClass(res)">{{ res.data.title }}</div>
                        <p v-if="res.data.summary" class="text-[11px] text-gray-500 leading-relaxed mt-0.5">{{ res.data.summary }}</p>
                        <button
                          v-if="evidenceFor(res).length"
                          class="mt-1 flex items-center gap-1 text-[10.5px] text-gray-400 hover:text-gray-600"
                          @click="toggleEvidence(res.id)"
                        >
                          <FontAwesomeIcon :icon="['fas', expandedEvidence[res.id] ? 'chevron-down' : 'chevron-right']" class="text-[9px]" />
                          {{ evidenceFor(res).length }} {{ evidenceFor(res).length === 1 ? 'source' : 'sources' }}
                        </button>
                        <div v-if="expandedEvidence[res.id]" class="mt-1 space-y-1">
                          <div v-for="(ev, i) in evidenceFor(res)" :key="i" class="flex gap-1.5 text-[10.5px]">
                            <FontAwesomeIcon :icon="evidenceIcon(ev.evidence_type)" class="text-gray-300 shrink-0 mt-0.5" />
                            <div class="min-w-0">
                              <span class="font-medium text-gray-500">{{ ev.source }}</span>
                              <span v-if="ev.text" class="text-gray-400"> — “{{ ev.text }}”</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Resolutions attached DIRECTLY to the signal (legacy pre-2026-07-15
                   shape, 117 rows). Shown so nothing is silently dropped. -->
              <div v-if="legacyResolutionsFor(sig.id).length" class="mt-1.5 ml-4 space-y-1.5">
                <div v-for="res in legacyResolutionsFor(sig.id)" :key="res.id" class="flex items-start gap-1.5">
                  <FontAwesomeIcon :icon="['fas', 'arrow-turn-down-right']" class="text-gray-300 text-[11px] mt-2.5 shrink-0" />
                  <div class="flex-1 min-w-0 rounded-lg border shadow-sm px-3 py-2 flex items-start gap-2" :class="resolutionBox(res)">
                    <FontAwesomeIcon :icon="['fas', resolutionIcon(res)]" class="text-xs shrink-0 mt-0.5" :class="resolutionIconClass(res)" />
                    <div class="min-w-0">
                      <span class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide inline-block mb-1" :class="resolutionBadge(res)">Resolution</span>
                      <div class="text-xs font-medium" :class="resolutionTextClass(res)">{{ res.data.title }}</div>
                      <p v-if="res.data.summary" class="text-[11px] text-gray-500 leading-relaxed">{{ res.data.summary }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Add to-do under this signal -->
              <div class="ml-4 mt-1.5">
                <div v-if="addingFor === sig.id" class="flex items-center gap-1.5">
                  <FontAwesomeIcon :icon="['fas', 'arrow-turn-down-right']" class="text-gray-300 text-[11px] shrink-0" />
                  <input
                    ref="addInput"
                    v-model="newTodoTitle"
                    type="text"
                    placeholder="New to-do…"
                    class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-1 focus:ring-primary-500"
                    @keyup.enter="submitTodo(sig.id)"
                    @keyup.esc="cancelAdd"
                  />
                  <button class="text-xs px-2 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50" :disabled="!newTodoTitle.trim()" @click="submitTodo(sig.id)">Add</button>
                  <button class="text-xs px-1.5 py-1.5 text-gray-400 hover:text-gray-600" @click="cancelAdd">Cancel</button>
                </div>
                <button
                  v-else
                  class="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-primary-600 pl-4"
                  @click="startAdd(sig.id)"
                >
                  <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[9px]" />
                  Add to-do
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- To-dos with no signal: handoffs between programs and requests from other teams -->
        <div v-if="orphanTodos.length" class="bg-white rounded-lg border border-gray-200 p-3">
          <div class="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Handoffs and requests</div>
          <div v-for="td in orphanTodos" :key="td.id" class="flex items-start gap-2 py-1.5 border-t border-gray-100 first:border-t-0">
            <span class="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" :class="todoDot(td)"></span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5 flex-wrap">
                <span class="text-xs" :class="todoClosed(td) ? 'text-gray-400 line-through' : 'text-gray-700'">{{ td.data.title }}</span>
                <span v-if="td.data.source?.author" class="text-[10.5px] text-gray-400">{{ td.data.source.author }}</span>
                <span v-if="td.data.due_date" class="text-[10.5px] text-gray-400">due {{ fmtDate(td.data.due_date) }}</span>
              </div>
              <p v-if="td.data.summary" class="text-[11px] text-gray-500 mt-0.5 leading-relaxed" data-demo-blur>{{ td.data.summary }}</p>
              <div class="mt-1">
                <CareGraphAssignee
                  :owner-entity-id="ownerFor(td.id)"
                  :owner-name="ownerNameFor(td.id)"
                  :members="members"
                  @assign="mid => onReassign(td.id, mid)"
                  @unassign="onReassign(td.id, null)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- FACTS strip (durable background, no status) -->
        <div v-if="facts.length" class="bg-white rounded-lg border border-gray-200">
          <button class="w-full flex items-center gap-2 px-3 py-2 text-left" @click="factsOpen = !factsOpen">
            <FontAwesomeIcon :icon="['fas', factsOpen ? 'chevron-down' : 'chevron-right']" class="text-[10px] text-gray-400" />
            <FontAwesomeIcon :icon="['fas', 'file-medical']" class="text-xs text-gray-500" />
            <span class="text-xs font-semibold text-gray-600">Background facts</span>
            <span class="text-xs text-gray-400 ml-auto">{{ facts.length }}</span>
          </button>
          <div v-if="factsOpen" class="px-3 pb-2 pl-8 space-y-1.5">
            <div v-for="f in facts" :key="f.id">
              <div class="text-xs text-gray-700">{{ f.data.title }}</div>
              <p v-if="f.data.summary" class="text-[11px] text-gray-500 leading-relaxed">{{ f.data.summary }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CareGraphAssignee from '@/components/Care_Management/CareGraphAssignee.vue';

// The three high-leverage loops the readmission analysis found protective.
// timely_followup and decompensation_escalation are deliberately EXCLUDED from the
// study pool (the first is collinear with the follow-up covariate, the second marks
// decline already in progress) — but they are still real barriers, so they are
// badged here; the pool distinction belongs in the analysis, not the chart.
const LEVERAGE_TYPES = new Set([
  'critical_med_access', 'home_services', 'redflag_routing',
  'timely_followup', 'decompensation_escalation',
]);

// Human labels for the model's enum vocabularies.
const LABELS = {
  critical_med_access: 'med access',
  timely_followup: 'timely follow-up',
  home_services: 'home services',
  decompensation_escalation: 'escalation',
  redflag_routing: 'red-flag routing',
  med_access: 'med access',
  med_adherence: 'med adherence',
  no_followup: 'no follow-up',
  decompensation: 'decompensation',
  transport: 'transport',
  caregiver: 'caregiver',
  dme: 'DME',
  other: 'other',
  hf: 'heart failure', ami: 'AMI', copd: 'COPD', stroke: 'stroke', pneumonia: 'pneumonia',
  anticoagulant: 'anticoagulant', insulin_antidiabetic: 'insulin/antidiabetic',
  diuretic: 'diuretic', antiarrhythmic: 'antiarrhythmic', antiseizure: 'antiseizure',
  active_infection_abx: 'antibiotics', hf_bp_med: 'HF/BP med',
};

export default {
  name: 'PatientCareGraph',
  components: { FontAwesomeIcon, CareGraphAssignee },

  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, required: true },
  },

  emits: ['show-message'],

  data() {
    return {
      loading: false,
      rebuilding: false,
      carePlanId: null,
      nodes: [],
      edges: [],
      expandedEvidence: {},
      expandedEpisodes: {},
      factsOpen: false,
      owners: {},
      members: [],
      addingFor: null,
      newTodoTitle: '',
      filter: 'all',
      newTodo: { open: false, mode: 'existing', signalId: '', newSignalTitle: '', title: '', summary: '', assignee: '' },
    };
  },

  computed: {
    canSubmitNewTodo() {
      if (!this.newTodo.title.trim()) return false;
      return this.newTodo.mode === 'existing'
        ? !!this.newTodo.signalId
        : !!this.newTodo.newSignalTitle.trim();
    },
    hasGraph() {
      return this.nodes.some(n => n.node_type.key !== 'evidence');
    },
    nodesById() {
      const m = {};
      this.nodes.forEach(n => { m[n.id] = n; });
      return m;
    },
    // Edge lookups built ONCE per graph load. Rebuilding them per signal inside the
    // template turned rendering into O(signals x edges) — noticeable on a patient
    // with 22 episodes.
    edgeIndex() {
      const idx = {
        causes: {},        // signal -> [todo]
        resolvesTodo: {},  // todo -> [resolution]
        resolvesSignal: {},// signal -> [resolution]  (legacy shape)
        surfacedIn: {},    // signal -> episode
        dueTo: {},         // signal -> [fact]
        relates: {},       // signal -> [signal]
        derived: {},       // node -> [evidence node]  (legacy shape)
      };
      const push = (bag, k, v) => { (bag[k] = bag[k] || []).push(v); };
      for (const e of this.edges) {
        const key = e.edge_type.key;
        const s = e.source_node, t = e.target_node;
        if (key === 'causes') push(idx.causes, s, t);
        else if (key === 'resolves') {
          const tgt = this.nodesById[t];
          if (tgt && tgt.node_type.key === 'todo') push(idx.resolvesTodo, t, s);
          else push(idx.resolvesSignal, t, s);
        }
        else if (key === 'surfaced_in') idx.surfacedIn[s] = t;
        else if (key === 'due_to') push(idx.dueTo, s, t);
        else if (key === 'relates_to') { push(idx.relates, s, t); push(idx.relates, t, s); }
        else if (key === 'derived_from') push(idx.derived, s, t);
      }
      return idx;
    },
    // Signals, each stamped with a DERIVED status and a leverage flag.
    signals() {
      return this.nodes
        .filter(n => n.node_type.key === 'signal')
        .map(n => ({
          ...n,
          _status: this.deriveStatus(n),
          _leverage: LEVERAGE_TYPES.has(n.data.slam_dunk_type),
        }));
    },
    facts() {
      // Facts explaining a signal are shown inline on that signal ("Because: …"),
      // so the bottom strip carries only the ones not already accounted for.
      const explained = new Set();
      Object.values(this.edgeIndex.dueTo).forEach(list => list.forEach(id => explained.add(id)));
      return this.nodes.filter(n => n.node_type.key === 'fact' && !explained.has(n.id));
    },
    openCount() {
      return this.signals.filter(s => s._status !== 'resolved').length;
    },
    orphanTodos() {
      const attached = new Set();
      Object.values(this.edgeIndex.causes).forEach(list => list.forEach(id => attached.add(id)));
      return this.nodes.filter(n => n.node_type.key === 'todo' && !attached.has(n.id));
    },
    // EPISODE GROUPING. Signals nest under the episode they `surfaced_in`; newest
    // discharge first. Signals with no episode fall into a trailing group rather
    // than disappearing.
    episodeGroups() {
      const byEpisode = {};
      const unassigned = [];
      for (const sig of this.signals) {
        const epId = this.edgeIndex.surfacedIn[sig.id];
        if (epId && this.nodesById[epId]) (byEpisode[epId] = byEpisode[epId] || []).push(sig);
        else unassigned.push(sig);
      }
      const groups = Object.entries(byEpisode).map(([epId, sigs]) => {
        const ep = this.nodesById[epId];
        const f = this.episodeFields(ep);
        return this.buildGroup(`ep-${epId}`, ep, f.date, f.type, f.facility, f.summary, sigs);
      });
      // Newest discharge first; undated episodes last.
      groups.sort((a, b) => (b.sortDate || '').localeCompare(a.sortDate || ''));
      if (unassigned.length) {
        groups.push(this.buildGroup('unassigned', null, null, null, null, null, unassigned));
      }
      return groups;
    },
    visibleGroups() {
      if (this.filter === 'all') return this.episodeGroups;
      return this.episodeGroups
        .map(g => ({ ...g, signals: g.signals.filter(s => this.matchesFilter(s)) }))
        .filter(g => g.signals.length);
    },
    filterOptions() {
      const s = this.signals;
      return [
        { key: 'all', label: 'All', count: s.length },
        { key: 'open', label: 'Open', count: s.filter(x => x._status !== 'resolved').length },
        { key: 'leverage', label: 'High-leverage', count: s.filter(x => x._leverage).length },
        { key: 'readmit', label: 'Readmit risk', count: s.filter(x => x.data.readmission_relevant).length },
      ];
    },
    allExpanded() {
      const groups = this.visibleGroups;
      return groups.length > 0 && groups.every(g => this.expandedEpisodes[g.key]);
    },
  },

  watch: {
    entityId(newId, oldId) {
      if (newId && newId !== oldId) this.loadGraph();
    },
  },

  async mounted() {
    await this.loadGraph();
    this.loadMembers();
    this.reattachRunningRebuild();
  },

  methods: {
    labelize(v) { return v ? (LABELS[v] || String(v).replace(/_/g, ' ')) : ''; },

    // ---- DERIVED SIGNAL STATUS -------------------------------------------------
    // signal.data.status is NULL on ~87% of signals (the one-process rewrite dropped
    // _recompute_signal_statuses), so it is derived here from to-do completion —
    // the documented rule. A stored status is only a fallback when there are no
    // to-dos to derive from.
    deriveStatus(sig) {
      const todos = (this.edgeIndex.causes[sig.id] || [])
        .map(id => this.nodesById[id])
        .filter(n => n && n.node_type.key === 'todo');
      if (!todos.length) return sig.data.status || 'not_addressed';

      let anyOpen = false, anyDone = false, allClosed = true;
      for (const td of todos) {
        const res = this.resolutionsForTodo(td.id);
        if (!res.length) { anyOpen = true; allClosed = false; continue; }
        if (res.some(r => this.resolutionDone(r))) anyDone = true;
      }
      if (allClosed && !anyOpen) return anyDone ? 'resolved' : 'unable_to_resolve';
      return 'open';
    },

    // ---- EPISODE ---------------------------------------------------------------
    // The episode payload is LLM-authored label/value pairs, so read it by label
    // rather than assuming positions.
    episodeFields(ep) {
      const out = { date: null, type: null, facility: null, summary: null };
      if (!ep) return out;
      for (const row of (ep.data.data || [])) {
        const l = String(row.label || '').toLowerCase();
        const v = row.value;
        if (!v || v === 'Not provided in opportunity' || v === 'Not specified in ADT') continue;
        if (l.includes('discharge date')) out.date = v;
        else if (l.includes('episode type')) out.type = v;
        else if (l.includes('facility')) out.facility = v;
        else if (l.includes('summary')) out.summary = v;
      }
      return out;
    },
    buildGroup(key, ep, date, type, facility, summary, sigs) {
      // High-leverage first, then open before resolved — the working order.
      const ordered = [...sigs].sort((a, b) => {
        if (a._leverage !== b._leverage) return a._leverage ? -1 : 1;
        return this.statusRank(a._status) - this.statusRank(b._status);
      });
      let terminalCount = 0;
      for (const s of sigs) {
        for (const tdId of (this.edgeIndex.causes[s.id] || [])) {
          for (const r of this.resolutionsForTodo(tdId)) {
            if (r.data.closure_grade === 'verified_terminal' && this.resolutionDone(r)) terminalCount++;
          }
        }
      }
      return {
        key,
        episode: ep,
        episodeType: type ? this.titleCase(type) : null,
        facility,
        summary,
        sortDate: date ? String(date).slice(0, 10) : '',
        dateLabel: date ? this.fmtDate(date) : (ep ? 'Date unknown' : 'Not tied to a discharge'),
        signals: ordered,
        openCount: sigs.filter(s => s._status === 'open').length,
        resolvedCount: sigs.filter(s => s._status === 'resolved').length,
        leverageCount: sigs.filter(s => s._leverage).length,
        terminalCount,
      };
    },
    toggleEpisode(key) {
      this.expandedEpisodes = { ...this.expandedEpisodes, [key]: !this.expandedEpisodes[key] };
    },
    toggleAllEpisodes() {
      const expand = !this.allExpanded;
      const next = {};
      this.visibleGroups.forEach(g => { next[g.key] = expand; });
      this.expandedEpisodes = { ...this.expandedEpisodes, ...next };
    },
    // Open the episodes worth looking at, collapse the archive. A patient can carry
    // 22 episodes; expanding all of them defeats the grouping.
    autoExpand() {
      const next = {};
      this.episodeGroups.forEach((g, i) => {
        next[g.key] = i < 2 || g.leverageCount > 0 || g.openCount > 0;
      });
      this.expandedEpisodes = next;
    },
    matchesFilter(sig) {
      if (this.filter === 'open') return sig._status !== 'resolved';
      if (this.filter === 'leverage') return sig._leverage;
      if (this.filter === 'readmit') return !!sig.data.readmission_relevant;
      return true;
    },
    signalMeta(sig) {
      const out = [];
      const d = sig.data;
      if (d.readmission_pathway) out.push({ label: 'pathway', value: this.labelize(d.readmission_pathway) });
      if (d.med_class) out.push({ label: 'med', value: this.labelize(d.med_class) });
      if (d.index_condition && d.index_condition !== 'other') out.push({ label: 'condition', value: this.labelize(d.index_condition) });
      if (typeof d.confidence === 'number') out.push({ label: 'confidence', value: `${Math.round(d.confidence * 100)}%` });
      return out;
    },

    // ---- RESOLUTION ------------------------------------------------------------
    // Current vocabulary is done | unable_to_complete. The legacy resolved |
    // unable_to_resolve values still exist on ~117 rows, so both are accepted.
    resolutionDone(res) {
      const o = res.data.outcome;
      return o === 'done' || o === 'resolved';
    },
    resolutionsForTodo(todoId) {
      return (this.edgeIndex.resolvesTodo[todoId] || [])
        .map(id => this.nodesById[id])
        .filter(n => n && n.node_type.key === 'resolution');
    },
    legacyResolutionsFor(signalId) {
      return (this.edgeIndex.resolvesSignal[signalId] || [])
        .map(id => this.nodesById[id])
        .filter(n => n && n.node_type.key === 'resolution');
    },
    // Only a VERIFIED TERMINAL closure reads as success. in_progress_step and
    // soft_action are the near-miss the readmission analysis found carries no
    // benefit, so they are amber rather than green — the visual has to reflect that.
    resolutionBox(res) {
      if (!this.resolutionDone(res)) return 'bg-red-50 border-red-200';
      return res.data.closure_grade === 'verified_terminal'
        ? 'bg-green-50 border-green-200'
        : 'bg-amber-50 border-amber-200';
    },
    resolutionIcon(res) {
      if (!this.resolutionDone(res)) return 'circle-xmark';
      return res.data.closure_grade === 'verified_terminal' ? 'circle-check' : 'circle-half-stroke';
    },
    resolutionIconClass(res) {
      if (!this.resolutionDone(res)) return 'text-red-500';
      return res.data.closure_grade === 'verified_terminal' ? 'text-green-600' : 'text-amber-600';
    },
    resolutionTextClass(res) {
      if (!this.resolutionDone(res)) return 'text-red-800';
      return res.data.closure_grade === 'verified_terminal' ? 'text-green-800' : 'text-amber-800';
    },
    resolutionBadge(res) {
      if (!this.resolutionDone(res)) return 'bg-red-100 text-red-700';
      return res.data.closure_grade === 'verified_terminal'
        ? 'bg-green-100 text-green-700'
        : 'bg-amber-100 text-amber-700';
    },
    gradeLabel(g) {
      return { verified_terminal: 'verified', in_progress_step: 'in progress', soft_action: 'soft' }[g] || g;
    },
    gradeClass(g) {
      return {
        verified_terminal: 'bg-green-100 text-green-700',
        in_progress_step: 'bg-amber-100 text-amber-700',
        soft_action: 'bg-gray-100 text-gray-500',
      }[g] || 'bg-gray-100 text-gray-500';
    },
    gradeHint(g) {
      return {
        verified_terminal: 'The terminal act is documented — the prescription was filled, the visit attended.',
        in_progress_step: 'A real step, but not the terminal act (referred, booked, triaged).',
        soft_action: 'Advice or instruction only — no completed action documented.',
      }[g] || '';
    },

    // ---- TO-DO -----------------------------------------------------------------
    // A to-do's state is its own status PLUS whether a resolution closed it. The old
    // "attempted = done" reading was wrong: attempted means the work was carried
    // out, closure is a separate, evidence-backed judgement.
    todoClosed(td) { return this.resolutionsForTodo(td.id).length > 0; },
    todoDot(td) {
      const res = this.resolutionsForTodo(td.id);
      if (res.some(r => this.resolutionDone(r))) return 'bg-green-500';
      if (res.length) return 'bg-red-400';
      return td.data.status === 'attempted' ? 'bg-blue-500' : 'border border-gray-400';
    },
    todoStateLabel(td) {
      const res = this.resolutionsForTodo(td.id);
      if (res.some(r => this.resolutionDone(r))) return 'complete';
      if (res.length) return 'unable';
      return td.data.status === 'attempted' ? 'attempted' : 'planned';
    },
    todoStateClass(td) {
      const res = this.resolutionsForTodo(td.id);
      if (res.some(r => this.resolutionDone(r))) return 'text-green-600';
      if (res.length) return 'text-red-500';
      return td.data.status === 'attempted' ? 'text-blue-500' : 'text-gray-400';
    },
    todosFor(signalId) {
      return (this.edgeIndex.causes[signalId] || [])
        .map(id => this.nodesById[id])
        .filter(n => n && n.node_type.key === 'todo')
        .sort((a, b) => (this.todoClosed(a) ? 1 : 0) - (this.todoClosed(b) ? 1 : 0));
    },

    // ---- OTHER GRAPH READS -----------------------------------------------------
    dueToFor(signalId) {
      return (this.edgeIndex.dueTo[signalId] || [])
        .map(id => this.nodesById[id])
        .filter(n => n && n.node_type.key === 'fact');
    },
    relatedFor(signalId) {
      const seen = new Set();
      return (this.edgeIndex.relates[signalId] || [])
        .map(id => this.nodesById[id])
        .filter(n => {
          if (!n || n.node_type.key !== 'signal' || seen.has(n.id)) return false;
          seen.add(n.id);
          return true;
        });
    },
    // Evidence is INLINE on the node (data.evidence[]). Legacy evidence NODES
    // reached via `derived_from` are folded in so older graphs still show sources.
    evidenceFor(node) {
      const inline = Array.isArray(node.data && node.data.evidence) ? node.data.evidence : [];
      const legacy = (this.edgeIndex.derived[node.id] || [])
        .map(id => this.nodesById[id])
        .filter(n => n && n.node_type.key === 'evidence')
        .map(n => n.data);
      return [...inline, ...legacy];
    },
    scrollToSignal(id) {
      const el = this.$el.querySelector(`[data-signal="${id}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      // The signal may sit in a collapsed episode — open it, then scroll.
      const grp = this.episodeGroups.find(g => g.signals.some(s => s.id === id));
      if (!grp) return;
      this.expandedEpisodes = { ...this.expandedEpisodes, [grp.key]: true };
      this.$nextTick(() => {
        const el2 = this.$el.querySelector(`[data-signal="${id}"]`);
        if (el2) el2.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    toggleEvidence(id) {
      this.expandedEvidence = { ...this.expandedEvidence, [id]: !this.expandedEvidence[id] };
    },

    // ---- PRESENTATION HELPERS --------------------------------------------------
    statusRank(status) {
      const m = { open: 0, not_addressed: 1, unable_to_resolve: 2, resolved: 3 };
      return m[status] ?? 0;
    },
    statusDot(status) {
      return {
        resolved: 'bg-green-500',
        open: 'bg-amber-500',
        unable_to_resolve: 'bg-red-400',
        not_addressed: 'bg-gray-400',
      }[status] || 'bg-gray-400';
    },
    statusPillClass(status) {
      return {
        resolved: 'bg-green-100 text-green-700',
        open: 'bg-amber-100 text-amber-700',
        unable_to_resolve: 'bg-red-100 text-red-700',
        not_addressed: 'bg-gray-100 text-gray-500',
      }[status] || 'bg-gray-100 text-gray-500';
    },
    statusLabel(status) {
      return {
        resolved: 'Resolved',
        open: 'Open',
        unable_to_resolve: 'Unresolved',
        not_addressed: 'Not addressed',
      }[status] || status || '';
    },
    titleCase(s) {
      return String(s).toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    // Parse Y/M/D literally — `new Date(iso)` shifts the calendar day by the
    // browser offset, which is how an evening discharge starts reading as tomorrow.
    fmtDate(d) {
      if (!d) return '';
      const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return String(d);
      const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${MON[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
    },
    evidenceIcon(type) {
      return {
        assessment_answer: ['fas', 'clipboard-question'],
        transcript_quote: ['fas', 'quote-left'],
        note: ['fas', 'note-sticky'],
        chart_event: ['fas', 'hospital'],
        data_gap: ['fas', 'circle-question'],
      }[type] || ['fas', 'file-lines'];
    },

    // ---- OWNERSHIP -------------------------------------------------------------
    ownerFor(nodeId) { return this.owners[nodeId]?.owner_entity_id || null; },
    ownerNameFor(nodeId) { return this.owners[nodeId]?.owner_name || null; },
    async loadMembers() {
      try {
        const { data } = await this.$axios.get('/api/careplannodes/addressees/');
        this.members = (data?.members || []).map(r => ({ id: r.id, name: r.name || 'Unknown', kind: r.kind }));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },
    async onReassign(nodeId, memberId) {
      const prev = this.owners[nodeId] ? { ...this.owners[nodeId] } : null;
      const m = this.members.find(x => String(x.id) === String(memberId));
      this.owners = { ...this.owners, [nodeId]: memberId
        ? { owner_entity_id: Number(memberId), owner_name: m ? m.name : null }
        : { owner_entity_id: null, owner_name: null } };
      try {
        if (memberId) {
          await this.$axios.post(`api/careplannodes/${nodeId}/assign/`, { member_id: Number(memberId) });
        } else {
          await this.$axios.post(`api/careplannodes/${nodeId}/unassign/`);
        }
      } catch {
        this.owners = { ...this.owners, [nodeId]: prev };
        this.$emit('show-message', 'Reassignment failed', 'error');
      }
    },

    // ---- MUTATIONS -------------------------------------------------------------
    openNewTodo() {
      this.newTodo = {
        open: true,
        mode: this.signals.length ? 'existing' : 'new',
        signalId: '', newSignalTitle: '', title: '', summary: '', assignee: '',
      };
    },
    toggleSignalMode() {
      this.newTodo.mode = this.newTodo.mode === 'existing' ? 'new' : 'existing';
    },
    async submitNewTodo() {
      if (!this.canSubmitNewTodo) return;
      const payload = { title: this.newTodo.title.trim() };
      if (this.newTodo.summary && this.newTodo.summary.trim()) payload.summary = this.newTodo.summary.trim();
      if (this.newTodo.assignee) payload.owner_member_id = Number(this.newTodo.assignee);
      if (this.newTodo.mode === 'existing') {
        payload.signal_id = this.newTodo.signalId;
      } else {
        payload.new_signal_title = this.newTodo.newSignalTitle.trim();
        payload.entity_id = this.entityId;
      }
      try {
        await this.$axios.post('api/careplannodes/add-todo/', payload);
        this.newTodo.open = false;
        await this.loadGraph();
      } catch {
        this.$emit('show-message', 'Could not add to-do', 'error');
      }
    },
    startAdd(signalId) {
      this.addingFor = signalId;
      this.newTodoTitle = '';
      this.$nextTick(() => {
        const el = this.$refs.addInput;
        (Array.isArray(el) ? el[0] : el)?.focus();
      });
    },
    cancelAdd() {
      this.addingFor = null;
      this.newTodoTitle = '';
    },
    async submitTodo(signalId) {
      const title = this.newTodoTitle.trim();
      if (!title) return;
      try {
        await this.$axios.post('api/careplannodes/add-todo/', { signal_id: signalId, title });
        this.cancelAdd();
        await this.loadGraph();
      } catch {
        this.$emit('show-message', 'Could not add to-do', 'error');
      }
    },
    async deleteTodo(nodeId) {
      try {
        await this.$axios.delete(`api/careplannodes/${nodeId}/`);
        await this.loadGraph();
      } catch {
        this.$emit('show-message', 'Could not delete to-do', 'error');
      }
    },

    // ---- LOAD / REBUILD --------------------------------------------------------
    async loadGraph() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get('api/careplannodes/graph/', {
          params: { entity_id: this.entityId, patient_identifier: this.patientIdentifier },
        });
        this.carePlanId = data.care_plan?.id || null;
        this.nodes = data.nodes || [];
        this.edges = data.edges || [];
        this.owners = data.owners || {};
        this.expandedEvidence = {};
        this.autoExpand();
      } catch (error) {
        console.error('Error loading care graph:', error);
        this.nodes = [];
        this.edges = [];
        this.owners = {};
      } finally {
        this.loading = false;
      }
    },
    async rebuildCarePlan() {
      if (this.rebuilding) return;
      this.rebuilding = true;
      try {
        let id = this.carePlanId;
        if (!id) {
          const { data } = await this.$axios.post('api/careplans/', { entity_id: this.entityId });
          id = data?.care_plan?.id;
        }
        if (!id) return;
        const { data } = await this.$axios.post(`api/careplans/${id}/rebuild/`);
        const result = await this._pollJob(data.job_id);
        if (result.stillRunning) {
          this.$emit('show-message', 'Still rebuilding — this can take a minute. The graph will refresh when it finishes.', 'info');
        }
        await this.loadGraph();
      } catch (error) {
        console.error('Care graph rebuild failed:', error);
        this.$emit('show-message', 'Rebuild failed', 'error');
      } finally {
        this.rebuilding = false;
      }
    },
    async reattachRunningRebuild() {
      if (!this.carePlanId || this.rebuilding) return;
      try {
        const { data } = await this.$axios.get(`/api/careplans/${this.carePlanId}/active-job/`);
        if (data && data.job_id) {
          this.rebuilding = true;
          this._pollJob(data.job_id)
            .then((result) => { if (!result.stillRunning) this.loadGraph(); })
            .catch(() => {})
            .finally(() => { this.rebuilding = false; });
        }
      } catch (e) { /* best-effort */ }
    },
    async _pollJob(jobId) {
      const maxAttempts = 150;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const { data } = await this.$axios.get(`/api/jobs/${jobId}/status/`);
        if (data.status === 'completed') return data.result_data;
        if (data.status === 'failed') throw new Error(data.error_message || 'Job failed');
      }
      return { stillRunning: true };
    },
  },
};
</script>

<style scoped>
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>
